import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.background.card};
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.primary.main};
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${theme.colors.primary.main};
  color: ${theme.colors.primary.contrast};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary.dark};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const SeasonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const SeasonCard = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
    border-color: ${theme.colors.primary.main};
  }

  ${props => props.isActive && `
    border-color: ${theme.colors.success.main};
    box-shadow: 0 0 0 2px ${theme.colors.success.main};
  `}
`;

const SeasonCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
`;

const SeasonName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const SeasonStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  ${props => props.status === 'active' && `
    background: ${theme.colors.success.light};
    color: ${theme.colors.success.dark};
  `}
  ${props => props.status === 'completed' && `
    background: ${theme.colors.secondary.light};
    color: ${theme.colors.secondary.dark};
  `}
`;

const SeasonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
`;

const SeasonStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid ${theme.colors.border.primary};
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatValue = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const SeasonActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: auto;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.main};
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: ${theme.colors.background.paper};
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.secondary};

  svg {
    width: 4rem;
    height: 4rem;
    margin: 0 auto 1rem;
    opacity: 0.5;
  }

  p {
    margin-bottom: 1.5rem;
  }
`;

const VesselSeasonsManagement = () => {
  const { vesselId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vessel, setVessel] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [seasonStats, setSeasonStats] = useState({});

  useEffect(() => {
    fetchData();
  }, [vesselId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch vessel
      const vesselResponse = await api.get(`/api/vessels/${vesselId}`);
      setVessel(vesselResponse.data);

      // Fetch seasons
      const seasonsResponse = await api.get(`/api/vessel-seasons/vessel/${vesselId}`);
      const seasonsList = seasonsResponse.data.seasons || [];
      setSeasons(seasonsList);

      // Fetch stats for each season
      const statsPromises = seasonsList.map(async (season) => {
        try {
          const response = await api.get(`/api/vessel-seasons/${season.id}/summary`);
          const summary = response.data.summary || {};
          return {
            seasonId: season.id,
            stats: {
              totalTrips: summary.total_trips || 0,
              totalDistanceNm: (summary.total_distance_nm || 0).toFixed(1),
              totalFuelLiters: (summary.total_fuel_used_liters || 0).toFixed(0),
              totalEngineHours: (summary.total_run_hours ?? summary.total_engine_hours ?? 0).toFixed(1),
            },
          };
        } catch (error) {
          return { seasonId: season.id, stats: null };
        }
      });

      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(({ seasonId, stats }) => {
        statsMap[seasonId] = stats;
      });
      setSeasonStats(statsMap);
    } catch (error) {
      console.error('Error fetching vessel seasons:', error);
      toast.error('Failed to load seasons');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSeason = (seasonId) => {
    navigate(`/vessels/${vesselId}/seasons/${seasonId}`);
  };

  const handleDeleteSeason = async (e, seasonId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this season?')) {
      try {
        await api.delete(`/api/vessel-seasons/${seasonId}`);
        toast.success('Season deleted successfully');
        fetchData();
      } catch (error) {
        console.error('Error deleting season:', error);
        toast.error('Failed to delete season');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container>
        <EmptyState>
          <CalendarDaysIcon />
          <p>Loading seasons...</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton to="/vessels">
            <ArrowLeftIcon className="w-5 h-5" />
            Back to Vessels
          </BackButton>
          <div>
            <Title>Seasons - {vessel?.vessel_name}</Title>
            <div style={{ fontSize: '0.875rem', color: theme.colors.text.secondary, marginTop: '0.5rem' }}>
              Manage boating seasons and view season summaries
            </div>
          </div>
        </HeaderLeft>
        <CreateButton onClick={() => navigate(`/vessels`)}>
          <PlusIcon />
          Create New Season
        </CreateButton>
      </Header>

      {seasons.length > 0 ? (
        <SeasonsGrid>
          {seasons.map((season) => (
            <SeasonCard
              key={season.id}
              isActive={season.status === 'active'}
              onClick={() => handleViewSeason(season.id)}
            >
              <SeasonCardHeader>
                <SeasonName>{season.name}</SeasonName>
                <SeasonStatus status={season.status}>
                  {season.status === 'active' && <CheckCircleIcon className="w-4 h-4" />}
                  {season.status}
                </SeasonStatus>
              </SeasonCardHeader>

              <SeasonDetails>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CalendarDaysIcon className="w-4 h-4" />
                  {formatDate(season.start_date)} - {formatDate(season.end_date)}
                </div>
                <div>Year: {season.year}</div>
              </SeasonDetails>

              {seasonStats[season.id] && (
                <SeasonStats>
                  <StatItem>
                    <StatLabel>Trips</StatLabel>
                    <StatValue>{seasonStats[season.id].totalTrips}</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Distance</StatLabel>
                    <StatValue>{seasonStats[season.id].totalDistanceNm} nm</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Fuel</StatLabel>
                    <StatValue>{seasonStats[season.id].totalFuelLiters}L</StatValue>
                  </StatItem>
                  <StatItem>
                    <StatLabel>Run Time</StatLabel>
                    <StatValue>{seasonStats[season.id].totalEngineHours}h</StatValue>
                  </StatItem>
                </SeasonStats>
              )}

              <SeasonActions>
                <ActionButton onClick={(e) => { e.stopPropagation(); handleViewSeason(season.id); }}>
                  <ChartBarIcon />
                  View Details
                </ActionButton>
                <ActionButton onClick={(e) => handleDeleteSeason(e, season.id)}>
                  <TrashIcon />
                  Delete
                </ActionButton>
              </SeasonActions>
            </SeasonCard>
          ))}
        </SeasonsGrid>
      ) : (
        <EmptyState>
          <CalendarDaysIcon />
          <p>No seasons created yet for this vessel</p>
          <CreateButton onClick={() => navigate(`/vessels`)}>
            <PlusIcon />
            Create Your First Season
          </CreateButton>
        </EmptyState>
      )}
    </Container>
  );
};

export default VesselSeasonsManagement;

